<template>
    <BaseSidebar
        ref="transactionSidebar"
        :is-open="isOpen"
        :on-close="closeSidebar"
        :full-screen-on-mobile="true"
        body-class="bg-[linear-gradient(180deg,var(--color-bg-base)_0%,var(--color-bg-snow)_55%)]"
        header-class="bg-transparent"
    >
        <template #header-left>
            <div class="flex items-center">
                <button
                    type="button"
                    class="flex p-2 items-start gap-2 rounded-md hover:bg-bg-base"
                    :class="{
                        'opacity-50 cursor-not-allowed': isFirstTransaction,
                    }"
                    :disabled="isFirstTransaction"
                    @click="$emit('navigate', 'prev')"
                >
                    <Icon icon="chevron-up" :width="20" :height="20" />
                </button>
                <button
                    type="button"
                    class="flex p-2 items-start gap-2 rounded-md hover:bg-bg-base ml-2"
                    :class="{
                        'opacity-50 cursor-not-allowed': isLastTransaction,
                    }"
                    :disabled="isLastTransaction"
                    @click="$emit('navigate', 'next')"
                >
                    <Icon icon="chevron-down" :width="20" :height="20" />
                </button>
                <div
                    class="flex pl-2 gap-2 justify-start items-start text-fg-tertiary text-sm font-normal leading-5"
                >
                    {{ currentIndex }} of {{ totalCount }} transactions
                </div>
            </div>
        </template>

        <div v-if="transaction" class="px-8 pb-8">
            <!-- Date -->
            <div class="text-center text-sm text-fg-tertiary mt-2 mb-4">
                {{ formatDate(transaction.transaction_date) }}
            </div>

            <!-- Merchant Header -->
            <div class="flex flex-col items-center justify-center mb-6">
                <!-- Merchant Icon -->
                <div
                    v-if="!transaction.merchant_logo_url"
                    class="w-16 h-16 rounded-full bg-bg-base flex items-center justify-center mb-4"
                >
                    <Icon
                        :icon="iconForCategory(transaction.category)"
                        class="w-6 h-6 text-fg-secondary"
                    />
                </div>
                <img
                    v-else
                    :src="transaction.merchant_logo_url"
                    :alt="transaction.merchant_name"
                    class="w-16 h-16 rounded-full object-cover mb-4"
                />

                <!-- Amount -->
                <div
                    class="text-3xl font-bold"
                    :class="isCredit ? 'text-green-600' : 'text-fg-primary'"
                >
                    {{ formatAmount(transaction.amount) }}
                </div>

                <!-- Merchant Name -->
                <h2
                    class="text-xl font-semibold text-fg-primary text-center mt-2"
                >
                    {{ transaction.merchant_name }}
                </h2>

                <!-- Card chip -->
                <div
                    class="flex items-center gap-2 text-sm text-fg-tertiary mt-2"
                >
                    <img
                        v-if="cardImageUrl"
                        :src="cardImageUrl"
                        :alt="transaction.card_name"
                        class="w-7 h-[18px] rounded-sm object-cover"
                    />
                    <div
                        v-else
                        class="w-7 h-[18px] rounded-sm bg-gradient-to-r from-gray-600 to-gray-400"
                    ></div>
                    <span>
                        {{ transaction.card_name
                        }}<span v-if="transaction.card_last_four">
                            ({{ transaction.card_last_four }})</span
                        >
                    </span>
                </div>

                <!-- Pending Badge -->
                <span
                    v-if="transaction.is_pending"
                    v-tooltip="
                        'Pending transaction — the final amount may change.'
                    "
                    class="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-700 mt-2"
                >
                    Pending
                </span>

                <!-- Hidden Badge -->
                <span
                    v-if="localTransaction?.is_hidden"
                    v-tooltip="'Transaction hidden from list'"
                    class="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium bg-bg-base text-fg-secondary mt-2"
                >
                    <Icon
                        icon="eye-off"
                        :width="14"
                        :height="14"
                        class="mr-1"
                    />
                    Hidden
                </span>
            </div>

            <!-- Edit Button and More Menu (like My Wallet) -->
            <div class="flex justify-center items-center gap-2 mb-6">
                <button
                    class="flex items-center gap-2 px-4 py-2 rounded-[4px] border border-border-base bg-bg-snow shadow-sm hover:bg-bg-subtle transition-colors duration-150"
                    type="button"
                    @click="requestEdit"
                >
                    <Icon
                        icon="pencil"
                        :width="20"
                        :height="20"
                        class="text-fg-primary"
                    />
                    <span class="text-fg-primary text-sm font-medium leading-5">
                        Edit
                    </span>
                </button>
                <div class="relative" ref="moreMenuContainer">
                    <button
                        class="flex items-center justify-center w-9 h-9 p-2 rounded-[4px] border border-border-base bg-bg-snow shadow-sm hover:bg-bg-subtle transition-colors duration-150"
                        type="button"
                        @click="showMoreMenu = !showMoreMenu"
                    >
                        <Icon icon="dots-vertical" :width="20" :height="20" />
                    </button>
                    <!-- Popover Menu -->
                    <div
                        v-if="showMoreMenu"
                        class="absolute right-0 mt-2 flex p-2 flex-col items-start border border-border-base rounded-lg bg-bg-snow shadow-sm z-50 min-w-[180px]"
                        @click.stop
                    >
                        <button
                            @click="handleHideTransaction"
                            class="flex w-full px-2 py-1.5 items-center gap-2 rounded hover:bg-bg-subtle transition-colors duration-150"
                        >
                            <Icon
                                :icon="
                                    localTransaction?.is_hidden
                                        ? 'eye-on'
                                        : 'eye-off'
                                "
                                :width="20"
                                :height="20"
                            />
                            <span
                                class="text-fg-primary text-sm font-normal leading-5"
                            >
                                {{
                                    localTransaction?.is_hidden
                                        ? 'Show transaction'
                                        : 'Hide transaction'
                                }}
                            </span>
                        </button>
                        <button
                            @click="requestDelete"
                            class="flex w-full px-2 py-1.5 items-center gap-2 rounded hover:bg-bg-subtle text-danger transition-colors duration-150"
                        >
                            <Icon icon="trash" :width="20" :height="20" />
                            <span class="text-sm font-normal leading-5">
                                Delete
                            </span>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Rewards (block hidden entirely when there are no rewards;
                 the optimization tip nests inside, or stands alone) -->
            <div
                v-if="hasRewards || showOptimizationTip"
                class="rounded-xl mb-6 overflow-hidden border"
                :class="
                    hasRewards
                        ? 'border-border-base bg-bg-snow'
                        : 'border-border-brand-muted'
                "
            >
                <div v-if="hasRewards" class="p-4">
                    <div class="flex items-center justify-between">
                        <div>
                            <div class="text-lg font-semibold text-fg-primary">
                                {{ formatNumber(calculatePoints()) }} points
                            </div>
                            <div class="body-md text-fg-tertiary mt-0.5">
                                <span
                                    class="inline-flex items-center px-1.5 py-0.5 rounded bg-bg-base text-fg-secondary text-xs font-medium mr-1"
                                >
                                    {{ getDisplayEarnRate() }}x
                                </span>
                                {{ localTransaction.category }}
                            </div>
                        </div>
                        <div class="flex items-center gap-2">
                            <img
                                v-if="programImageUrl"
                                :src="programImageUrl"
                                :alt="localTransaction.program?.name"
                                class="w-auto h-6 object-contain"
                            />
                            <button
                                type="button"
                                class="p-1 rounded hover:bg-bg-base"
                                @click="requestEarnRateEdit"
                            >
                                <Icon
                                    icon="pencil"
                                    :width="16"
                                    :height="16"
                                    class="text-fg-tertiary"
                                />
                            </button>
                        </div>
                    </div>
                    <!-- Inline earn-rate edit -->
                    <div v-if="showEarnRateEdit" class="mt-3">
                        <label
                            class="block text-xs font-medium text-fg-secondary mb-1"
                        >
                            Earn rate
                        </label>
                        <select
                            v-model="localTransaction.earn_rate"
                            class="w-full px-3 py-2 text-sm border border-border-base rounded-lg bg-bg-snow text-fg-primary appearance-none cursor-pointer hover:border-border-emphasized focus:border-orange-500 focus:ring-1 focus:ring-orange-500 focus:outline-none"
                            @change="handleEarnRateChange"
                        >
                            <option
                                v-for="earnRateOption in availableEarnRates"
                                :key="earnRateOption.rate"
                                :value="earnRateOption.rate"
                            >
                                {{ earnRateOption.label }}
                            </option>
                        </select>
                    </div>
                </div>

                <!-- Points missed callout (nested under the rewards row, or
                     standalone when there is no rewards block) -->
                <div
                    v-if="showOptimizationTip"
                    class="bg-brand-orange-bg-light p-4"
                    :class="{ 'border-t border-border-brand-muted/50': hasRewards }"
                >
                    <div class="flex items-start gap-3">
                        <div class="flex-1 min-w-0">
                            <div
                                class="flex items-center gap-1.5 text-fg-brand font-semibold text-sm"
                            >
                                <Icon icon="sparkles" :width="16" :height="16" />
                                {{ formatNumber(bestAlternative?.points_delta || 0) }}
                                points missed
                            </div>
                            <p class="text-sm text-slate-700 mt-1">
                                Use
                                <span class="font-medium">{{
                                    bestAlternative?.card_name
                                }}</span>
                                for {{ bestAlternative?.earn_rate }}x
                                {{ localTransaction.category }}
                            </p>
                            <a
                                :href="bestCardsUrl"
                                class="inline-flex items-center gap-1 text-sm font-medium text-fg-brand mt-2"
                            >
                                Learn more
                                <Icon icon="chevron-right" :width="14" :height="14" />
                            </a>
                        </div>
                        <div
                            class="w-12 h-8 rounded-sm bg-gradient-to-r from-gray-600 to-gray-400 flex-shrink-0"
                        ></div>
                    </div>
                </div>
            </div>

            <!-- Details Section (like My Wallet) -->
            <div class="relative sm:px-0">
                <h4
                    class="flex items-center gap-3 self-stretch text-fg-primary text-base font-medium leading-6 mb-2"
                >
                    Details
                </h4>

                <dl class="w-full">
                    <!-- Statement -->
                    <div
                        class="py-2 grid grid-cols-[3fr_5fr] gap-1 items-start min-h-[36px]"
                    >
                        <dt
                            class="text-sm font-normal leading-5 text-fg-tertiary flex items-center gap-2"
                        >
                            <Icon icon="document" class="w-5 h-5" />
                            Statement
                        </dt>
                        <dd
                            class="text-sm text-left font-normal leading-5 text-fg-primary flex items-start gap-2"
                        >
                            <span class="min-w-0 break-words">
                                {{ statementText }}
                            </span>
                            <button
                                class="p-1 hover:bg-bg-base rounded flex-shrink-0"
                                @click="copyToClipboard(statementText)"
                            >
                                <Icon
                                    icon="copy"
                                    :width="14"
                                    :height="14"
                                    class="text-fg-tertiary"
                                />
                            </button>
                        </dd>
                    </div>

                    <!-- Category -->
                    <div
                        class="py-2 grid grid-cols-[3fr_5fr] gap-1 items-center min-h-[36px]"
                    >
                        <dt
                            class="text-sm font-normal leading-5 text-fg-tertiary flex items-center gap-2"
                        >
                            <Icon icon="tag" class="w-5 h-5" />
                            Category
                        </dt>
                        <dd
                            class="text-sm text-left font-normal leading-5 text-fg-primary"
                        >
                            {{ localTransaction.category }}
                        </dd>
                    </div>
                </dl>
            </div>

            <!-- Notes -->
            <div class="mt-6">
                <div class="flex items-center justify-between mb-2">
                    <h4
                        class="text-fg-primary text-base font-medium leading-6"
                    >
                        Notes
                    </h4>
                    <button
                        type="button"
                        class="text-sm font-medium text-10x-orange"
                        @click="requestEdit"
                    >
                        {{ localTransaction.notes ? 'Edit' : 'Add' }}
                    </button>
                </div>
                <p
                    v-if="localTransaction.notes"
                    class="text-sm text-fg-secondary whitespace-pre-line"
                >
                    {{ localTransaction.notes }}
                </p>
                <p v-else class="text-sm text-fg-tertiary">No notes yet.</p>
            </div>

            <!-- Files -->
            <div class="mt-6">
                <h4
                    class="text-fg-primary text-base font-medium leading-6 mb-2"
                >
                    Files
                </h4>
                <div
                    v-if="localTransaction.attachments?.length"
                    class="space-y-2 mb-2"
                >
                    <div
                        v-for="(
                            attachment, idx
                        ) in localTransaction.attachments"
                        :key="idx"
                        class="flex items-center justify-between p-2 bg-bg-subtle rounded-lg"
                    >
                        <div class="flex items-center gap-2">
                            <Icon
                                icon="document"
                                :width="16"
                                :height="16"
                                class="text-fg-tertiary"
                            />
                            <span class="text-sm text-fg-secondary">
                                {{ attachment.name }}
                            </span>
                        </div>
                        <button
                            @click="removeAttachment(idx)"
                            class="text-fg-tertiary hover:text-danger"
                        >
                            <Icon icon="close" :width="16" :height="16" />
                        </button>
                    </div>
                </div>
                <button
                    type="button"
                    class="flex items-center gap-2 px-4 py-2 rounded-[4px] border border-border-base bg-bg-snow shadow-sm hover:bg-bg-subtle transition-colors duration-150"
                    @click="handleAddAttachment"
                >
                    <Icon
                        icon="attachment"
                        :width="18"
                        :height="18"
                        class="text-fg-primary"
                    />
                    <span class="text-fg-primary text-sm font-medium leading-5">
                        Add files
                    </span>
                </button>
            </div>

            <!-- Benefit Tracking Section (MY1-2251) -->
            <div class="mt-6">
                <h4
                    class="flex items-center gap-3 self-stretch text-fg-primary text-base font-medium leading-6 mb-2"
                >
                    Benefit tracking
                </h4>

                <div
                    v-if="linkedBenefits.length"
                    class="flex flex-col gap-2 mb-3"
                >
                    <div
                        v-for="link in linkedBenefits"
                        :key="link.uuid"
                        class="flex items-center justify-between gap-2 rounded-lg border border-border-base bg-bg-subtle px-3 py-2"
                    >
                        <div class="flex items-center gap-2 min-w-0">
                            <Icon
                                icon="circle-check"
                                :width="18"
                                :height="18"
                                class="text-green-600 flex-shrink-0"
                            />
                            <span
                                class="truncate text-sm font-normal leading-5 text-fg-primary"
                            >
                                {{ link.benefit_title }}
                            </span>
                        </div>
                        <button
                            type="button"
                            class="p-1 rounded hover:bg-bg-base text-fg-tertiary hover:text-danger disabled:opacity-50"
                            :disabled="benefitActionLoading"
                            @click="unlinkBenefit(link.uuid)"
                        >
                            <Icon icon="trash" :width="16" :height="16" />
                        </button>
                    </div>
                </div>
                <!-- Suggested benefits (MY1-2825): one-tap confirm/dismiss -->
                <div
                    v-if="!linkedBenefits.length && suggestedBenefits.length"
                    class="flex flex-col gap-2 mb-3"
                >
                    <p class="text-xs font-normal leading-4 text-fg-tertiary">
                        Suggested for this transaction
                    </p>
                    <div
                        v-for="suggestion in suggestedBenefits"
                        :key="suggestion.card_benefit_id"
                        class="flex flex-col gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2"
                    >
                        <div class="flex items-start gap-2 min-w-0">
                            <Icon
                                icon="sparkles"
                                :width="18"
                                :height="18"
                                class="text-amber-500 flex-shrink-0 mt-0.5"
                            />
                            <div class="min-w-0 flex-1">
                                <div class="flex items-center gap-2">
                                    <span
                                        class="truncate text-sm font-medium leading-5 text-fg-primary"
                                    >
                                        {{ suggestion.benefit_title }}
                                    </span>
                                    <span
                                        class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide"
                                        :class="
                                            confidenceChipClass(
                                                suggestion.confidence_label
                                            )
                                        "
                                    >
                                        {{ suggestion.confidence_label }}
                                    </span>
                                </div>
                                <p
                                    class="text-xs font-normal leading-4 text-fg-tertiary mt-0.5"
                                >
                                    {{ suggestion.reason }}
                                </p>
                            </div>
                        </div>
                        <div class="flex items-center gap-2 justify-end">
                            <button
                                type="button"
                                class="px-3 py-1 text-xs font-medium text-fg-secondary rounded hover:bg-amber-100 transition-colors disabled:opacity-50"
                                :disabled="benefitActionLoading"
                                @click="
                                    dismissSuggestion(suggestion.card_benefit_id)
                                "
                            >
                                Dismiss
                            </button>
                            <button
                                type="button"
                                class="px-3 py-1 text-xs font-medium text-white bg-orange-500 rounded hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                :disabled="benefitActionLoading"
                                @click="confirmSuggestion(suggestion)"
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>

                <p
                    v-else-if="!linkedBenefits.length"
                    class="text-sm font-normal leading-5 text-fg-tertiary mb-3"
                >
                    Not linked to a benefit yet.
                </p>

                <button
                    type="button"
                    class="flex items-center gap-2 px-4 py-2 rounded-[4px] border border-border-base bg-bg-snow shadow-sm hover:bg-bg-subtle transition-colors duration-150"
                    @click="openBenefitModal"
                >
                    <Icon
                        icon="plus"
                        :width="18"
                        :height="18"
                        class="text-fg-primary"
                    />
                    <span class="text-fg-primary text-sm font-medium leading-5">
                        Link to a benefit
                    </span>
                </button>
            </div>

        </div>
    </BaseSidebar>

    <!-- Delete Confirmation Modal -->
    <div
        v-if="showDeleteConfirm"
        class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]"
        @click.self="showDeleteConfirm = false"
    >
        <div class="bg-bg-snow rounded-xl p-6 max-w-sm mx-4 shadow-xl">
            <div class="flex items-center gap-3 mb-4">
                <div
                    class="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center"
                >
                    <Icon
                        icon="trash"
                        :width="20"
                        :height="20"
                        class="text-red-600"
                    />
                </div>
                <h3 class="text-lg font-semibold text-fg-primary">
                    Delete Transaction?
                </h3>
            </div>
            <p class="text-sm text-fg-secondary mb-6">
                This transaction will be hidden from your spending history. You
                can restore it later from settings.
            </p>
            <div class="flex gap-3">
                <button
                    class="flex-1 px-4 py-2 text-sm font-medium text-fg-secondary bg-bg-base rounded-lg hover:bg-slate-200 transition-colors"
                    @click="showDeleteConfirm = false"
                >
                    Cancel
                </button>
                <button
                    class="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                    @click="handleDeleteTransaction"
                >
                    Delete
                </button>
            </div>
        </div>
    </div>

    <!-- Edit Transaction Modal -->
    <UiModal
        :is-open="showEditModal"
        title="Edit transaction details"
        width="480px"
        @close="showEditModal = false"
    >
        <template #subtitle>
            <span class="text-sm font-normal text-fg-tertiary">
                {{ transaction?.card_name
                }}<span v-if="transaction?.card_last_four">
                    ({{ transaction.card_last_four }})</span
                >
                · {{ formatAmount(transaction?.amount) }}
            </span>
        </template>
        <template #content>
            <div class="space-y-4">
                <!-- Merchant Field -->
                <div>
                    <label
                        class="block text-sm font-medium text-fg-secondary mb-1"
                    >
                        Merchant
                    </label>
                    <input
                        type="text"
                        v-model="editForm.merchant"
                        class="w-full px-3 py-2 text-sm border rounded-lg bg-bg-snow focus:ring-1 focus:outline-none"
                        :class="
                            editErrors.merchant
                                ? 'border-danger focus:border-danger focus:ring-danger'
                                : 'border-border-base hover:border-slate-300 focus:border-orange-500 focus:ring-orange-500'
                        "
                        @input="editErrors.merchant = ''"
                    />
                    <p
                        v-if="editErrors.merchant"
                        class="mt-1 flex items-center gap-1 text-xs text-danger"
                    >
                        <Icon icon="warning" :width="12" :height="12" />
                        {{ editErrors.merchant }}
                    </p>
                </div>

                <!-- Date Field -->
                <div>
                    <label
                        class="block text-sm font-medium text-fg-secondary mb-1"
                    >
                        Date
                    </label>
                    <input
                        type="date"
                        v-model="editForm.transaction_date"
                        class="w-full px-3 py-2 text-sm border rounded-lg bg-bg-snow focus:ring-1 focus:outline-none"
                        :class="
                            editErrors.transaction_date
                                ? 'border-danger focus:border-danger focus:ring-danger'
                                : 'border-border-base hover:border-slate-300 focus:border-orange-500 focus:ring-orange-500'
                        "
                        @input="editErrors.transaction_date = ''"
                        @change="editErrors.transaction_date = ''"
                    />
                    <p
                        v-if="editErrors.transaction_date"
                        class="mt-1 flex items-center gap-1 text-xs text-danger"
                    >
                        <Icon icon="warning" :width="12" :height="12" />
                        {{ editErrors.transaction_date }}
                    </p>
                </div>

                <!-- Category Field -->
                <div>
                    <label
                        class="block text-sm font-medium text-fg-secondary mb-1"
                    >
                        Category
                    </label>
                    <!-- Custom listbox (v2 spec): category icon + label per option,
                         icons shared with Best Cards by Category via iconForCategory. -->
                    <div ref="categoryDropdownContainer" class="relative">
                        <button
                            type="button"
                            class="w-full flex items-center gap-2 px-3 py-2 text-sm border rounded-lg bg-bg-snow cursor-pointer focus:ring-1 focus:outline-none"
                            :class="
                                editErrors.category
                                    ? 'border-danger focus:border-danger focus:ring-danger'
                                    : 'border-border-base hover:border-slate-300 focus:border-orange-500 focus:ring-orange-500'
                            "
                            aria-haspopup="listbox"
                            :aria-expanded="showCategoryDropdown"
                            @click="showCategoryDropdown = !showCategoryDropdown"
                            @keydown.esc.stop="showCategoryDropdown = false"
                        >
                            <Icon
                                :icon="iconForCategory(editForm.category)"
                                class="w-5 h-5 text-fg-secondary shrink-0"
                            />
                            <span
                                class="flex-1 text-left text-fg-primary truncate"
                            >
                                {{ editForm.category || 'Select category' }}
                            </span>
                            <Icon
                                icon="chevron-down"
                                :width="16"
                                :height="16"
                                class="text-fg-tertiary shrink-0 transition-transform"
                                :class="{ 'rotate-180': showCategoryDropdown }"
                            />
                        </button>
                        <ul
                            v-if="showCategoryDropdown"
                            class="absolute z-10 mt-2 w-full max-h-64 overflow-y-auto p-2 bg-bg-snow border border-border-base rounded-lg shadow-sm"
                            role="listbox"
                        >
                            <li v-for="cat in categories" :key="cat">
                                <button
                                    type="button"
                                    class="w-full flex items-center gap-2 p-2 rounded text-sm text-fg-primary text-left hover:bg-bg-subtle focus:bg-bg-subtle focus:outline-none transition-colors duration-150"
                                    :class="{
                                        'bg-bg-subtle':
                                            cat === editForm.category,
                                    }"
                                    role="option"
                                    :aria-selected="cat === editForm.category"
                                    @click="selectCategory(cat)"
                                >
                                    <Icon
                                        :icon="iconForCategory(cat)"
                                        class="w-5 h-5 text-fg-secondary shrink-0"
                                    />
                                    <span class="truncate">{{ cat }}</span>
                                </button>
                            </li>
                        </ul>
                    </div>
                    <p
                        v-if="editErrors.category"
                        class="mt-1 flex items-center gap-1 text-xs text-danger"
                    >
                        <Icon icon="warning" :width="12" :height="12" />
                        {{ editErrors.category }}
                    </p>
                </div>

                <!-- Earn rate + Reward points -->
                <div class="grid grid-cols-2 gap-3">
                    <div>
                        <label
                            class="block text-sm font-medium text-fg-secondary mb-1"
                        >
                            Earn rate
                        </label>
                        <div class="relative">
                            <select
                                v-model="editForm.earn_rate"
                                :disabled="editForm.override_points"
                                class="w-full px-3 py-2 text-sm border border-border-base rounded-lg bg-bg-snow appearance-none cursor-pointer hover:border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 focus:outline-none pr-10 disabled:bg-bg-subtle disabled:text-fg-tertiary"
                                @change="recalcRewardPoints"
                            >
                                <option
                                    v-for="earnRateOption in availableEarnRates"
                                    :key="earnRateOption.rate"
                                    :value="earnRateOption.rate"
                                >
                                    {{ earnRateOption.label }}
                                </option>
                            </select>
                            <Icon
                                icon="chevron-down"
                                :width="16"
                                :height="16"
                                class="absolute right-3 top-1/2 -translate-y-1/2 text-fg-tertiary pointer-events-none"
                            />
                        </div>
                    </div>
                    <div>
                        <label
                            class="block text-sm font-medium text-fg-secondary mb-1"
                        >
                            Reward points
                        </label>
                        <div class="relative">
                            <input
                                type="number"
                                min="0"
                                v-model.number="editForm.reward_points"
                                :disabled="!editForm.override_points"
                                class="w-full px-3 py-2 pr-9 text-sm border border-border-base rounded-lg bg-bg-snow hover:border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 focus:outline-none disabled:bg-bg-subtle disabled:text-fg-tertiary"
                            />
                            <img
                                v-if="programImageUrl"
                                :src="programImageUrl"
                                :alt="localTransaction.program?.name"
                                class="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-auto object-contain pointer-events-none"
                            />
                        </div>
                    </div>
                </div>
                <label
                    class="flex items-center gap-2 text-sm font-normal text-fg-secondary cursor-pointer"
                >
                    <input
                        type="checkbox"
                        v-model="editForm.override_points"
                        class="rounded text-10x-orange"
                        @change="recalcRewardPoints"
                    />
                    Override points
                </label>

                <!-- Notes Field -->
                <div>
                    <label
                        class="block text-sm font-medium text-fg-secondary mb-1"
                    >
                        Notes (optional)
                    </label>
                    <textarea
                        v-model="editForm.notes"
                        placeholder="Write your notes here"
                        rows="3"
                        class="w-full px-3 py-2 text-sm border border-border-base rounded-lg bg-bg-snow resize-none hover:border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 focus:outline-none"
                    ></textarea>
                </div>

                <!-- Files -->
                <div>
                    <label
                        class="block text-sm font-medium text-fg-secondary mb-1"
                    >
                        Files (optional)
                    </label>
                    <div
                        class="border-2 border-dashed border-border-base rounded-lg p-6 text-center hover:border-slate-300 cursor-pointer transition-colors"
                        @click="handleAddAttachment"
                    >
                        <p class="text-sm text-fg-secondary">
                            Upload or drag &amp; drop photos, screenshots, and
                            more
                        </p>
                        <p class="text-xs text-fg-tertiary mt-1">
                            We support PNG, JPEG, DOC, PDF under 10 MB
                        </p>
                        <button
                            type="button"
                            class="mt-3 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-fg-secondary border border-border-base rounded-lg hover:bg-bg-subtle transition-colors"
                        >
                            <Icon
                                icon="upload"
                                :width="16"
                                :height="16"
                                class="text-fg-tertiary"
                            />
                            Upload
                        </button>
                    </div>
                </div>
            </div>
        </template>

        <template #footer>
            <div class="flex gap-3 justify-end">
                <button
                    class="px-6 py-2 text-sm font-medium text-fg-primary bg-bg-snow border border-border-base rounded-lg hover:bg-bg-subtle transition-colors"
                    @click="showEditModal = false"
                >
                    Cancel
                </button>
                <button
                    class="px-6 py-2 text-sm font-medium text-fg-inverted bg-bg-intense rounded-lg hover:opacity-90 transition-opacity"
                    @click="saveEditForm"
                >
                    Save
                </button>
            </div>
        </template>
    </UiModal>

    <!-- Link to Benefit Modal (MY1-2251) -->
    <UiModal
        :is-open="showBenefitModal"
        title="Link to a benefit"
        width="480px"
        @close="closeBenefitModal"
    >
        <template #content>
            <div class="space-y-4">
                <p class="text-sm text-fg-tertiary">
                    Count this transaction toward a benefit on
                    <span class="font-medium text-fg-secondary">{{
                        transaction?.card_name
                    }}</span>
                    for usage tracking.
                </p>

                <div
                    v-if="benefitOptionsLoading"
                    class="py-6 text-center text-sm text-fg-tertiary"
                >
                    Loading benefits…
                </div>

                <div
                    v-else-if="!availableBenefits.length"
                    class="py-6 text-center text-sm text-fg-tertiary"
                >
                    {{
                        benefitError ||
                        'No trackable benefits are available on this card.'
                    }}
                </div>

                <div v-else class="space-y-3">
                    <div
                        class="max-h-60 overflow-y-auto flex flex-col gap-1 pr-1"
                    >
                        <label
                            v-for="benefit in availableBenefits"
                            :key="benefit.id"
                            class="flex items-center gap-3 px-3 py-2 rounded-lg border cursor-pointer transition-colors"
                            :class="[
                                isBenefitLinked(benefit.id)
                                    ? 'border-slate-100 bg-bg-subtle opacity-60 cursor-not-allowed'
                                    : selectedBenefitId === benefit.id
                                      ? 'border-orange-500 bg-orange-50'
                                      : 'border-border-base hover:border-slate-300',
                            ]"
                        >
                            <input
                                type="radio"
                                name="benefit-option"
                                class="text-orange-500 focus:ring-orange-500"
                                :value="benefit.id"
                                v-model="selectedBenefitId"
                                :disabled="isBenefitLinked(benefit.id)"
                            />
                            <span class="flex-1 min-w-0">
                                <span
                                    class="block text-sm font-medium text-fg-primary truncate"
                                >
                                    {{ benefit.benefit_title }}
                                </span>
                                <span
                                    v-if="isBenefitLinked(benefit.id)"
                                    class="block text-xs text-fg-tertiary"
                                >
                                    Already linked
                                </span>
                            </span>
                        </label>
                    </div>

                    <!-- Amount Field -->
                    <div>
                        <label
                            class="block text-sm font-medium text-fg-secondary mb-1"
                        >
                            Amount
                        </label>
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            v-model="benefitAmount"
                            :placeholder="`Defaults to ${Math.abs(
                                transaction?.amount || 0
                            ).toFixed(2)}`"
                            class="w-full px-3 py-2 text-sm border border-border-base rounded-lg bg-bg-snow hover:border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 focus:outline-none"
                        />
                    </div>

                    <!-- Notes Field -->
                    <div>
                        <label
                            class="block text-sm font-medium text-fg-secondary mb-1"
                        >
                            Notes
                        </label>
                        <textarea
                            v-model="benefitNotes"
                            placeholder="Optional notes about this usage…"
                            rows="2"
                            class="w-full px-3 py-2 text-sm border border-border-base rounded-lg bg-bg-snow resize-none hover:border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 focus:outline-none"
                        ></textarea>
                    </div>

                    <p v-if="benefitError" class="text-sm text-danger">
                        {{ benefitError }}
                    </p>
                </div>
            </div>
        </template>

        <template #footer>
            <div class="flex gap-3 justify-end">
                <button
                    class="px-6 py-2 text-sm font-medium text-fg-secondary bg-bg-base rounded-lg hover:bg-slate-200 transition-colors"
                    @click="closeBenefitModal"
                >
                    Cancel
                </button>
                <button
                    class="px-6 py-2 text-sm font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    :disabled="!selectedBenefitId || benefitActionLoading"
                    @click="submitBenefitLink"
                >
                    {{ benefitActionLoading ? 'Linking…' : 'Link benefit' }}
                </button>
            </div>
        </template>
    </UiModal>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, onUnmounted } from 'vue';
import BaseSidebar from '@/components/ui/base-sidebar.vue';
import { iconForCategory } from '@/utils/category-icons';
import Icon from '@/components/ui/icon/icon.vue';
import UiModal from '@/components/ui/modal/ui-modal.vue';
import { Apii } from '@/services/api.js';

const ApiService = new Apii();

const props = defineProps({
    isOpen: {
        type: Boolean,
        default: false,
    },
    transaction: {
        type: Object,
        default: null,
    },
    currentIndex: {
        type: Number,
        default: 0,
    },
    totalCount: {
        type: Number,
        default: 0,
    },
    // When false (free users), edit/mutation actions open the upgrade prompt.
    canEdit: {
        type: Boolean,
        default: true,
    },
    categories: {
        type: Array,
        default: () => [
            'Shopping',
            'Restaurants & Bars',
            'Groceries',
            'Travel',
            'Entertainment & Recreation',
            'Gas & Fuel',
            'Home & Utilities',
            'Health & Wellness',
            'Transfer',
        ],
    },
});

const emit = defineEmits([
    'close',
    'navigate',
    'update',
    'delete',
    'hide',
    'upgrade',
]);

// Edit/mutation gates — free users (canEdit=false) get the upgrade prompt.
const requestEdit = () => {
    resetEditErrors();
    showCategoryDropdown.value = false;
    if (!props.canEdit) {
        showMoreMenu.value = false;
        emit('upgrade');
        return;
    }
    showEditModal.value = true;
};

const requestEarnRateEdit = () => {
    if (!props.canEdit) {
        emit('upgrade');
        return;
    }
    showEarnRateEdit.value = !showEarnRateEdit.value;
};

const requestDelete = () => {
    showMoreMenu.value = false;
    if (!props.canEdit) {
        emit('upgrade');
        return;
    }
    showDeleteConfirm.value = true;
};

// Local state
const showMoreMenu = ref(false);
const showDeleteConfirm = ref(false);
const showEarnRateEdit = ref(false);
const showEditModal = ref(false);
const moreMenuContainer = ref(null);

// Category listbox in the edit modal (v2 spec: icons per option)
const showCategoryDropdown = ref(false);
const categoryDropdownContainer = ref(null);
const selectCategory = (cat) => {
    editForm.category = cat;
    editErrors.category = '';
    showCategoryDropdown.value = false;
};

// Benefit linking for usage tracking (MY1-2251)
const showBenefitModal = ref(false);
const benefitOptionsLoading = ref(false);
const benefitActionLoading = ref(false);
const availableBenefits = ref([]);
const linkedBenefits = ref([]);
const suggestedBenefits = ref([]);
const selectedBenefitId = ref(null);
const benefitAmount = ref('');
const benefitNotes = ref('');
const benefitError = ref('');

// Edit form state
const editForm = reactive({
    merchant: '',
    transaction_date: '',
    category: '',
    notes: '',
    earn_rate: 1,
    reward_points: 0,
    override_points: false,
});

// Local copy of transaction for editing
const localTransaction = ref({
    category: '',
    earn_rate: 1,
    notes: '',
    attachments: [],
    is_hidden: false,
    is_deleted: false,
});

// Watch for transaction changes and update local state
watch(
    () => props.transaction,
    (newTransaction) => {
        if (newTransaction) {
            // A user-edited category (client-side `category`) wins over the
            // API's merchant_category, so saved edits survive the prop
            // round-trip instead of being reverted by this watcher.
            const category =
                newTransaction.category ||
                newTransaction.merchant_category ||
                'Other';

            localTransaction.value = {
                ...newTransaction,
                category: category,
                // Use user-set earn_rate if available, otherwise use calculated rate from backend
                earn_rate:
                    newTransaction.earn_rate ||
                    newTransaction.calculated_earn_rate ||
                    1,
                calculated_earn_rate: newTransaction.calculated_earn_rate || 1,
                earn_rate_category: newTransaction.earn_rate_category || null,
                is_bonus_category: newTransaction.is_bonus_category || false,
                base_spend_amount: newTransaction.base_spend_amount || 1,
                available_earn_rates: newTransaction.available_earn_rates || [
                    { rate: 1, label: '1x (Base rate)', is_base: true },
                ],
                program: newTransaction.program || null,
                notes: newTransaction.notes || '',
                attachments: newTransaction.attachments || [],
                is_hidden: newTransaction.is_hidden || false,
                is_deleted: newTransaction.is_deleted || false,
                // Store original description for display
                original_description:
                    newTransaction.original_description ||
                    newTransaction.description ||
                    '',
            };
            // Track which benefits this transaction is linked to (MY1-2251)
            linkedBenefits.value = Array.isArray(newTransaction.linked_benefits)
                ? [...newTransaction.linked_benefits]
                : [];
            // Track suggested (unconfirmed) benefit matches (MY1-2825)
            suggestedBenefits.value = Array.isArray(
                newTransaction.suggested_benefits
            )
                ? [...newTransaction.suggested_benefits]
                : [];
            // Update edit form
            editForm.merchant = newTransaction.merchant_name || '';
            editForm.transaction_date = newTransaction.transaction_date || '';
            editForm.category = category;
            editForm.notes = newTransaction.notes || '';
            editForm.earn_rate =
                newTransaction.earn_rate ||
                newTransaction.calculated_earn_rate ||
                1;
            editForm.override_points = false;
            editForm.reward_points = Math.floor(
                Math.abs(newTransaction.amount || 0) * editForm.earn_rate
            );
            // Reset edit mode when transaction changes
            showEarnRateEdit.value = false;
            showEditModal.value = false;
        }
    },
    { immediate: true }
);

// Close menu when clicking outside
const handleClickOutside = (event) => {
    if (
        moreMenuContainer.value &&
        !moreMenuContainer.value.contains(event.target)
    ) {
        showMoreMenu.value = false;
    }
    if (
        categoryDropdownContainer.value &&
        !categoryDropdownContainer.value.contains(event.target)
    ) {
        showCategoryDropdown.value = false;
    }
};

onMounted(() => {
    document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
});

const isFirstTransaction = computed(() => props.currentIndex <= 1);
const isLastTransaction = computed(
    () => props.currentIndex >= props.totalCount
);
// Best alternative card for this transaction (from the backend). Drives the
// "points missed" callout — shown only when a wallet card would earn more.
const bestAlternative = computed(
    () => props.transaction?.best_alternative ?? null
);

// Rewards block gating (v2 spec: hide the block entirely when there are no
// rewards — no matched card, credits, or zero amounts).
const hasRewards = computed(() => {
    const t = props.transaction || {};
    const amount = Math.abs(t.amount || 0);
    const isExpense = t.is_expense || t.type === 'DEBIT';

    return (
        amount > 0 &&
        isExpense &&
        !!t.card_name &&
        t.card_name !== 'Unknown Card'
    );
});
const showOptimizationTip = computed(() => !!bestAlternative.value);

// Computed property for available earn rates from the card
const availableEarnRates = computed(() => {
    return (
        localTransaction.value.available_earn_rates || [
            { rate: 1, label: '1x (Base rate)', is_base: true },
        ]
    );
});

// Construct program image URL from program data
const programImageUrl = computed(() => {
    const programImage = localTransaction.value.program?.image;
    if (!programImage?.path || !programImage?.file_name) {
        return null;
    }
    const cdnUrl = window.cdnUrl || '';
    return `${cdnUrl}/${programImage.path}/_small/${programImage.file_name}`;
});

// Construct card image URL from card_image data (for the header card chip).
const cardImageUrl = computed(() => {
    const cardImage = props.transaction?.card_image;
    if (!cardImage?.path || !cardImage?.file_name) {
        return null;
    }
    const cdnUrl = window.cdnUrl || '';
    return `${cdnUrl}/${cardImage.path}/_small/${cardImage.file_name}`;
});

// Original statement line shown in Details (synthesized fallback).
const statementText = computed(() => {
    if (localTransaction.value.original_description) {
        return localTransaction.value.original_description;
    }
    const merchant = props.transaction?.merchant_name || '';
    const lastFour = props.transaction?.card_last_four;
    return lastFour ? `${merchant} #${lastFour}` : merchant;
});

// Link target for the "points missed" callout's Learn more link.
const bestCardsUrl = computed(() => {
    try {
        return route('wallet.best-cards');
    } catch (e) {
        return '/wallet/best-cards';
    }
});


// Methods
const closeSidebar = () => {
    showMoreMenu.value = false;
    emit('close');
};




// Warehouse amounts are positive magnitudes; the debit/credit distinction
// lives in the is_income / is_expense flags (mirrors TransactionRow).
const isCredit = computed(() => {
    const transaction = props.transaction || {};
    if (transaction.is_income) {
        return true;
    }
    if (transaction.type === 'CREDIT') {
        return true;
    }
    if (transaction.is_expense) {
        return false;
    }
    return transaction.amount < 0;
});

const formatAmount = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) {
        return '';
    }
    const formatted = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    }).format(Math.abs(amount));
    return isCredit.value ? `+${formatted}` : formatted;
};

const formatDate = (dateStr) => {
    if (!dateStr) {
        return '';
    }
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });
};

const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(Math.round(num));
};

// Rewards calculations
const calculatePoints = () => {
    // A manual points override is shown verbatim; deriving it back from the
    // stored rate can drop a point to float error (floor(41.13 * (100/41.13))).
    if (Number.isFinite(localTransaction.value.reward_points_override)) {
        return localTransaction.value.reward_points_override;
    }
    const amount = Math.abs(props.transaction?.amount || 0);
    const rate = getDisplayEarnRate();
    return Math.floor(amount * rate);
};

// Get the earn rate to display - user override takes precedence, then calculated rate from backend
const getDisplayEarnRate = () => {
    // If user has manually set an earn rate, use that
    if (localTransaction.value.earn_rate) {
        return localTransaction.value.earn_rate;
    }
    // Otherwise use the calculated rate from the backend
    return localTransaction.value.calculated_earn_rate || 1;
};

const handleEarnRateChange = () => {
    emitUpdate();
};

// Keep the Reward points field in sync with the earn rate unless the user is
// manually overriding it.
const recalcRewardPoints = () => {
    if (editForm.override_points) {
        return;
    }
    editForm.reward_points = Math.floor(
        Math.abs(props.transaction?.amount || 0) * editForm.earn_rate
    );
};

const handleAddAttachment = () => {
    // Placeholder - would open file picker in real implementation
};

const removeAttachment = (idx) => {
    localTransaction.value.attachments.splice(idx, 1);
    emitUpdate();
};

const copyToClipboard = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
    } catch {
        // Clipboard permission denied / unavailable — nothing to recover.
    }
};

// More menu actions
const handleHideTransaction = () => {
    showMoreMenu.value = false;
    if (!props.canEdit) {
        emit('upgrade');
        return;
    }
    localTransaction.value.is_hidden = !localTransaction.value.is_hidden;
    emit('hide', {
        id: props.transaction.id,
        is_hidden: localTransaction.value.is_hidden,
    });
};

const handleDeleteTransaction = () => {
    showDeleteConfirm.value = false;
    showMoreMenu.value = false;
    localTransaction.value.is_deleted = true;
    emit('delete', {
        id: props.transaction.id,
    });
    closeSidebar();
};

const emitUpdate = () => {
    emit('update', {
        id: props.transaction.id,
        ...localTransaction.value,
    });
};

// Benefit linking (MY1-2251)
const linkedBenefitIds = computed(
    () => new Set(linkedBenefits.value.map((b) => b.card_benefit_id))
);

const isBenefitLinked = (benefitId) => linkedBenefitIds.value.has(benefitId);

// Keep the local list in sync and let the parent list reflect the change so the
// row badge stays accurate.
const syncLinkedBenefits = (linked) => {
    linkedBenefits.value = Array.isArray(linked) ? linked : [];
    emit('update', {
        id: props.transaction.id,
        linked_benefits: linkedBenefits.value,
    });
};

// Keep suggestions in sync with the parent list so the row badge updates.
const syncSuggestions = (suggestions) => {
    suggestedBenefits.value = Array.isArray(suggestions) ? suggestions : [];
    emit('update', {
        id: props.transaction.id,
        suggested_benefits: suggestedBenefits.value,
    });
};

// Confidence chip colors (high → green, medium → amber, low → slate).
const confidenceChipClass = (label) => {
    if (label === 'high') {
        return 'bg-green-100 text-green-700';
    }
    if (label === 'medium') {
        return 'bg-amber-100 text-amber-700';
    }
    return 'bg-bg-base text-fg-secondary';
};

// Confirm a suggested benefit (MY1-2825): links it via the existing endpoint.
const confirmSuggestion = async (suggestion) => {
    if (benefitActionLoading.value) {
        return;
    }
    benefitActionLoading.value = true;
    benefitError.value = '';

    try {
        const { data } = await ApiService.apiCall(
            `/transactions/${props.transaction.id}/link-benefit`,
            'post',
            { card_benefit_id: suggestion.card_benefit_id }
        );
        // Once linked, the transaction is no longer "suggested".
        syncSuggestions([]);
        syncLinkedBenefits(data?.linked || []);
    } catch (e) {
        benefitError.value =
            e?.response?.data?.message || 'Could not link this benefit.';
    } finally {
        benefitActionLoading.value = false;
    }
};

// Dismiss a suggestion locally (no server state for v1).
const dismissSuggestion = (benefitId) => {
    syncSuggestions(
        suggestedBenefits.value.filter(
            (s) => s.card_benefit_id !== benefitId
        )
    );
};

const openBenefitModal = async () => {
    showMoreMenu.value = false;
    benefitError.value = '';
    selectedBenefitId.value = null;
    benefitAmount.value = '';
    benefitNotes.value = '';
    availableBenefits.value = [];
    showBenefitModal.value = true;
    benefitOptionsLoading.value = true;

    try {
        const { data } = await ApiService.apiCall(
            `/transactions/${props.transaction.id}/benefit-options`,
            'get'
        );
        availableBenefits.value = data?.data?.benefits || [];
        if (data?.data?.linked) {
            syncLinkedBenefits(data.data.linked);
        }
        if (!availableBenefits.value.length && data?.message) {
            benefitError.value = data.message;
        }
    } catch (e) {
        benefitError.value = 'Could not load benefits for this card.';
    } finally {
        benefitOptionsLoading.value = false;
    }
};

const closeBenefitModal = () => {
    showBenefitModal.value = false;
};

const submitBenefitLink = async () => {
    if (!selectedBenefitId.value || benefitActionLoading.value) {
        return;
    }
    benefitActionLoading.value = true;
    benefitError.value = '';

    try {
        const payload = { card_benefit_id: selectedBenefitId.value };
        if (benefitAmount.value !== '') {
            payload.amount = Number(benefitAmount.value);
        }
        if (benefitNotes.value.trim() !== '') {
            payload.notes = benefitNotes.value.trim();
        }

        const { data } = await ApiService.apiCall(
            `/transactions/${props.transaction.id}/link-benefit`,
            'post',
            payload
        );
        syncSuggestions([]);
        syncLinkedBenefits(data?.linked || []);
        showBenefitModal.value = false;
    } catch (e) {
        benefitError.value =
            e?.response?.data?.message || 'Could not link this benefit.';
    } finally {
        benefitActionLoading.value = false;
    }
};

const unlinkBenefit = async (uuid) => {
    if (benefitActionLoading.value) {
        return;
    }
    benefitActionLoading.value = true;
    benefitError.value = '';

    try {
        const { data } = await ApiService.apiCall(
            `/transactions/${props.transaction.id}/link-benefit/${uuid}`,
            'delete'
        );
        syncLinkedBenefits(data?.linked || []);
    } catch (e) {
        benefitError.value = 'Could not remove this benefit link.';
    } finally {
        benefitActionLoading.value = false;
    }
};

// Field-level validation for the edit modal (v2 "List of errors" spec).
const editErrors = reactive({
    merchant: '',
    transaction_date: '',
    category: '',
});

const resetEditErrors = () => {
    editErrors.merchant = '';
    editErrors.transaction_date = '';
    editErrors.category = '';
};

const validateEditForm = () => {
    editErrors.merchant = editForm.merchant?.trim()
        ? ''
        : 'Enter merchant name';
    editErrors.transaction_date = editForm.transaction_date
        ? ''
        : 'Select date';
    editErrors.category = editForm.category ? '' : 'Select category';

    return (
        !editErrors.merchant &&
        !editErrors.transaction_date &&
        !editErrors.category
    );
};

// Save edit form
const saveEditForm = () => {
    if (!validateEditForm()) {
        return;
    }

    // Update local transaction with form values
    localTransaction.value.merchant_name = editForm.merchant;
    localTransaction.value.transaction_date = editForm.transaction_date;
    localTransaction.value.category = editForm.category;
    localTransaction.value.notes = editForm.notes;

    // Persist the points choice as an effective earn rate so the rewards card
    // reflects it, and keep the raw override so it displays verbatim. A cleared
    // or non-numeric points input falls back to the selected earn rate rather
    // than poisoning earn_rate with NaN.
    const amount = Math.abs(props.transaction?.amount || 0);
    const overridePoints = Number(editForm.reward_points);
    if (
        editForm.override_points &&
        amount > 0 &&
        Number.isFinite(overridePoints) &&
        overridePoints >= 0
    ) {
        localTransaction.value.earn_rate = overridePoints / amount;
        localTransaction.value.reward_points_override = overridePoints;
    } else {
        localTransaction.value.earn_rate = editForm.earn_rate;
        localTransaction.value.reward_points_override = null;
    }

    // Emit update
    emitUpdate();

    // Close modal
    showEditModal.value = false;
};
</script>
